import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Save, X } from "lucide-react";

const empty = { name: "", price: 0, duration_days: 30, description: "", perks: [], order: 0, accent: "#FF1493" };

export default function AdminTiers() {
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [perkInput, setPerkInput] = useState("");

  const load = () => http.get("/tiers").then((r) => setTiers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), duration_days: parseInt(form.duration_days) };
      if (editing === "new") await http.post("/tiers", payload);
      else await http.put(`/tiers/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); setForm(empty); setPerkInput("");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await http.delete(`/tiers/${id}`);
    load();
  };

  const startEdit = (t) => { setEditing(t.id); setForm({ ...t }); };
  const addPerk = () => {
    if (!perkInput.trim()) return;
    setForm({ ...form, perks: [...form.perks, perkInput.trim()] });
    setPerkInput("");
  };
  const removePerk = (idx) => setForm({ ...form, perks: form.perks.filter((_, i) => i !== idx) });

  const cls = "w-full bg-noir-base border border-rosegold/20 px-3 py-2 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne";

  return (
    <div data-testid="admin-tiers-page">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Memberships</div>
          <h1 className="font-display text-4xl text-champagne">Tiers & <span className="italic text-pink-hot">perks</span></h1>
        </div>
        <button onClick={() => { setEditing("new"); setForm({ ...empty, order: tiers.length }); }} className="px-5 py-3 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow flex items-center gap-2" data-testid="add-tier-btn">
          <Plus className="w-4 h-4" /> New Tier
        </button>
      </div>

      {editing && (
        <div className="glass-panel rounded-md p-6 mb-6 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <input className={cls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className={cls} type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <input className={cls} type="number" placeholder="Duration (days)" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
            <input className={cls} placeholder="Accent color (hex)" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} />
          </div>
          <textarea className={cls} rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="space-y-2">
            <div className="text-[10px] tracking-[0.28em] uppercase text-pink-blush">Perks</div>
            <div className="flex gap-2">
              <input className={cls} placeholder="Add a perk" value={perkInput} onChange={(e) => setPerkInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPerk())} />
              <button onClick={addPerk} className="px-4 bg-pink-hot/20 text-pink-hot text-xs rounded-sm">Add</button>
            </div>
            <div className="space-y-1">
              {form.perks.map((p, i) => (
                <div key={i} className="flex justify-between items-center text-sm text-champagne/80 bg-noir-base px-3 py-2 rounded-sm border border-rosegold/10">
                  <span>✦ {p}</span>
                  <button onClick={() => removePerk(i)} className="text-rosegold/70 hover:text-pink-hot"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="px-5 py-2 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setEditing(null); setForm(empty); }} className="px-5 py-2 border border-rosegold/30 text-champagne text-xs tracking-[0.28em] uppercase rounded-sm flex items-center gap-2"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {tiers.map((t) => (
          <div key={t.id} className="glass-panel rounded-md p-5" style={{ borderColor: `${t.accent}40` }}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: t.accent }}>{t.name}</div>
                <div className="font-display text-3xl text-champagne mt-1">${t.price}</div>
                <div className="text-xs text-champagne/60">/ {t.duration_days}d</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(t)} className="p-2 text-champagne/70 hover:text-pink-hot"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(t.id)} className="p-2 text-champagne/70 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-champagne/70 mb-3">{t.description}</p>
            <ul className="text-xs text-champagne/80 space-y-1">
              {t.perks.map((p, i) => <li key={i}>✦ {p}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
